import { buttonVariants } from '@edsn/pie-components';
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckIcon, DashIcon } from '@radix-ui/react-icons';
import { useId } from 'react';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { InfoTooltip } from '../info-tooltip/InfoTooltip';

export interface InputCheckboxProps
  extends Omit<Checkbox.CheckboxProps, 'onChange' | 'value'>,
    Omit<VariantProps<typeof inputCheckbox>, 'checked'> {
  label?: string;
  onChange?: (value?: string) => void;
  value?: string;
  card?: boolean;
  tooltip?: string;
  isRequired?: boolean;
}

export const InputCheckbox = function InputCheckbox({
  ref,
  label,
  isError,
  onCheckedChange,
  onChange,
  card,
  tooltip,
  ...props
}: InputCheckboxProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) {
  const { disabled, value, checked } = props;
  const styles = inputCheckbox({
    card,
    checked: !!checked,
    disabled,
    isError,
  });
  const Icon = checked === 'indeterminate' ? DashIcon : CheckIcon;

  const id = useId();

  return (
    <label htmlFor={id} className={styles.wrapper()}>
      <Checkbox.Root
        ref={ref}
        id={id}
        className={styles.root()}
        onCheckedChange={(checkedState) => {
          onCheckedChange?.(checkedState);
          onChange?.(checkedState !== false ? value : undefined);
        }}
        aria-label={label}
        {...props}
      >
        <Checkbox.Indicator className="flex">
          <Icon className={styles.checker()} />
        </Checkbox.Indicator>
      </Checkbox.Root>
      <span className={styles.label()}>
        {label}
        {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
      </span>
    </label>
  );
};

const inputCheckbox = tv({
  slots: {
    checker: 'text-content-reversed',
    label: 'text-content-body cursor-pointer',
    root: 'flex items-center justify-center w-4 h-4 rounded-input appearance-none bg-surface-base border border-border-subtle-enabled text-content-body hover:border-border-subtle-hover focus-visible:outline-border-subtle-focus active:border-border-subtle-active disabled:border-border-disabled disabled:text-content-disabled data-[state=checked]:bg-action-primary-enabled data-[state=checked]:border-border-action-primary-enabled data-[state=checked]:text-content-reversed data-[state=checked]:hover:border-border-subtle-hover data-[state=checked]:focus-visible:outline-border-subtle-focus data-[state=checked]:active:border-border-subtle-active data-[state=checked]:disabled:bg-checkbox-disabled data-[state=checked]:disabled:border-checkbox-disabled data-[state=checked]:disabled:text-content-disabled data-[state=indeterminate]:bg-action-primary-enabled data-[state=indeterminate]:border-action-primary-enabled data-[state=indeterminate]:text-content-reversed data-[state=indeterminate]:hover:border-border-subtle-hover data-[state=indeterminate]:focus-visible:outline-border-subtle-focus data-[state=indeterminate]:active:border-border-subtle-active data-[state=indeterminate]:disabled:bg-checkbox-disabled data-[state=indeterminate]:disabled:border-checkbox-disabled data-[state=indeterminate]:disabled:text-content-disabled',
    wrapper: 'flex gap-2 items-center pl-px',
  },
  variants: {
    card: {
      true: {
        wrapper: cn(
          buttonVariants({ action: 'tertiary', emphasis: 'subtle' }),
          'py-3 px-3 justify-start cursor-pointer w-full',
        ),
      },
    },
    checked: {
      true: {
        root: 'bg-action-primary-enabled outline-action-primary-enabled focus-visible:outline-primary border-primary shadow-none',
      },
    },
    disabled: {
      true: {
        checker: 'text-content-disabled',
        label: 'text-content-disabled cursor-not-allowed',
        root: 'bg-neutral-200 data-[state=checked]:bg-neutral-200 data-[state=checked]:outline-neutral-400 data-[state=checked]:outline-1 outline-neutral-400',
        wrapper: 'hover:bg-transparant cursor-not-allowed',
      },
    },
    isError: {
      true: {
        label: 'text-content-error',
        root: 'data-[state=checked]:bg-support-error-bold data-[state=checked]:outline-support-error-bold bg-surface-error outline-support-error-bold',
      },
    },
  },
});
